import React, { useState, useEffect, useCallback } from 'react';
import { listApiKeys, createApiKey, revealApiKey, revokeApiKey, ApiKey, ApiKeyCreated, API_BASE } from '../api';
import { useAuth } from '../AuthContext';

const ALL_SCOPES: { value: string; label: string; hint: string }[] = [
  { value: 'orders:read', label: 'Orders', hint: 'Read orders and line items' },
  { value: 'inventory:read', label: 'Inventory', hint: 'Read FBA and FBM inventory' },
];

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const fmtDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';

/** Robust copy that works on localhost and falls back when the Clipboard API is blocked. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

// ── Icons ──────────────────────────────────────────────────────────────────────
const IconKey = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);
const IconCopy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// ── Reusable copy button ────────────────────────────────────────────────────────
const CopyButton: React.FC<{ text: string; label?: string; variant?: 'primary' | 'ghost'; title?: string }> = ({ text, label, variant = 'ghost', title }) => {
  const [done, setDone] = useState(false);
  const onClick = async () => {
    if (await copyText(text)) {
      setDone(true);
      setTimeout(() => setDone(false), 1600);
    }
  };
  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
    fontSize: 12.5, fontWeight: 600, borderRadius: 7, transition: 'all .15s',
    padding: label ? '7px 12px' : '6px', lineHeight: 1,
  };
  const styles: React.CSSProperties = variant === 'primary'
    ? { ...base, background: done ? 'var(--success, #16a34a)' : 'var(--accent)', color: '#fff', border: '1px solid transparent' }
    : { ...base, background: 'transparent', color: done ? 'var(--success, #16a34a)' : 'var(--text-muted)', border: '1px solid var(--border)' };
  return (
    <button onClick={onClick} style={styles} title={title || 'Copy'}>
      {done ? <IconCheck /> : <IconCopy />}
      {label ? <span>{done ? 'Copied' : label}</span> : null}
    </button>
  );
};

// Copies the FULL key for an existing row by fetching it from the reveal endpoint
// on demand (so secrets aren't shipped in the list payload). Falls back to the
// prefix for legacy keys created before reveal support.
const RevealCopyButton: React.FC<{ apiKey: ApiKey; onError: (m: string) => void }> = ({ apiKey, onError }) => {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      let text = apiKey.key_prefix;
      if (apiKey.is_revealable) {
        const res = await revealApiKey(apiKey.id);
        text = res.data.api_key;
      }
      if (await copyText(text)) {
        setDone(true);
        setTimeout(() => setDone(false), 1600);
      } else {
        onError('Copy failed — clipboard blocked by the browser');
      }
    } catch (e: any) {
      onError(e.response?.data?.detail || 'Failed to reveal key');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onClick}
      disabled={busy}
      title={apiKey.is_revealable ? 'Copy full API key' : 'Copy key prefix (full key unavailable — created before reveal support)'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: busy ? 'default' : 'pointer',
        fontSize: 12, fontWeight: 600, borderRadius: 7, padding: '6px 10px', lineHeight: 1,
        background: 'transparent', color: done ? 'var(--success, #16a34a)' : 'var(--text-muted)',
        border: '1px solid var(--border)', opacity: busy ? 0.6 : 1, transition: 'all .15s',
      }}
    >
      {done ? <IconCheck /> : <IconCopy />}
      <span>{done ? 'Copied' : busy ? '…' : 'Copy'}</span>
    </button>
  );
};

// ── API reference ────────────────────────────────────────────────────────────

type Param = { name: string; type: string; required?: boolean; desc: string; example?: string };

const InlineCode: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code style={{ fontSize: 12, padding: '2px 6px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{children}</code>
);

const ParamTable: React.FC<{ params: Param[] }> = ({ params }) => (
  <div style={{ overflow: 'auto', border: '1px solid var(--border)', borderRadius: 9, marginTop: 10 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 640 }}>
      <thead>
        <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
          {['Parameter', 'Type', 'Description'].map((h) => (
            <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, fontSize: 10.5, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {params.map((p) => (
          <tr key={p.name} style={{ borderBottom: '1px solid var(--border)', verticalAlign: 'top' }}>
            <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
              <code style={{ fontSize: 12, fontWeight: 600, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'var(--text-primary)' }}>{p.name}</code>
              {p.required && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--danger, #dc2626)' }}>required</span>}
            </td>
            <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: 'var(--text-muted)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5 }}>{p.type}</td>
            <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>
              {p.desc}
              {p.example && <div style={{ marginTop: 4 }}>e.g. <InlineCode>{p.example}</InlineCode></div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const EndpointRow: React.FC<{ path: string }> = ({ path }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5, color: 'var(--success, #16a34a)', border: '1px solid color-mix(in srgb, var(--success, #16a34a) 40%, transparent)', borderRadius: 5, padding: '2px 7px', flexShrink: 0 }}>GET</span>
    <code style={{ fontSize: 12.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{path}</code>
  </div>
);

const ExampleRequest: React.FC<{ label: string; url: string }> = ({ label, url }) => {
  const curl = `curl "${url}" -H "X-API-Key: art_live_…"`;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 5 }}>{label}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <code style={{ flex: 1, display: 'block', padding: '10px 13px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 9, fontSize: 12, wordBreak: 'break-all', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{curl}</code>
        <CopyButton text={curl} title="Copy example request" />
      </div>
    </div>
  );
};

const ORDER_PARAMS: Param[] = [
  { name: 'seller_id', type: 'string', desc: 'Restrict to a single SP-API account. Omit to include every account the key can read.', example: 'AVIYOQ3KB9CRN' },
  { name: 'marketplace_id', type: 'integer', desc: 'Restrict to one marketplace.', example: '1' },
  { name: 'status', type: 'string', desc: 'Filter by order status (exact match).', example: 'Shipped' },
  { name: 'purchase_date_from', type: 'ISO-8601', desc: 'Only orders whose purchase_date is on or after this instant (inclusive).', example: '2026-01-01' },
  { name: 'purchase_date_to', type: 'ISO-8601', desc: 'Only orders whose purchase_date is on or before this instant (inclusive).', example: '2026-06-30T23:59:59Z' },
  { name: 'date_field', type: 'enum', desc: 'Column used by date_from / date_to and for sorting: purchase_date (default) or last_update_date.' },
  { name: 'date_from', type: 'ISO-8601', desc: 'Generic lower bound applied to date_field (inclusive).' },
  { name: 'date_to', type: 'ISO-8601', desc: 'Generic upper bound applied to date_field (inclusive).' },
  { name: 'page', type: 'integer', desc: 'Page number, 1-based. Default 1.' },
  { name: 'page_size', type: 'integer', desc: 'Rows per page. Default 100, maximum 1000.', example: '1000' },
];

const FBA_PARAMS: Param[] = [
  { name: 'seller_id', type: 'string', desc: 'Restrict to a single SP-API account.', example: 'AVIYOQ3KB9CRN' },
  { name: 'marketplace_id', type: 'integer', desc: 'Restrict to one marketplace.', example: '1' },
  { name: 'asin', type: 'string', desc: 'Filter by ASIN (exact match).' },
  { name: 'sku', type: 'string', desc: 'Filter by seller SKU (exact match).' },
  { name: 'stock_status', type: 'enum', desc: 'in_stock (fulfillable > 0) or out_of_stock (fulfillable = 0).' },
  { name: 'page', type: 'integer', desc: 'Page number, 1-based. Default 1.' },
  { name: 'page_size', type: 'integer', desc: 'Rows per page. Default 100, maximum 1000.' },
];

const FBM_PARAMS: Param[] = [
  { name: 'seller_id', type: 'string', desc: 'Restrict to a single SP-API account.', example: 'AVIYOQ3KB9CRN' },
  { name: 'marketplace_id', type: 'integer', desc: 'Restrict to one marketplace.', example: '1' },
  { name: 'asin', type: 'string', desc: 'Filter by ASIN (exact match).' },
  { name: 'sku', type: 'string', desc: 'Filter by seller SKU (exact match).' },
  { name: 'status', type: 'string', desc: 'Filter by listing status, e.g. Active / Inactive.' },
  { name: 'page', type: 'integer', desc: 'Page number, 1-based. Default 1.' },
  { name: 'page_size', type: 'integer', desc: 'Rows per page. Default 100, maximum 1000.' },
];

const ApiReference: React.FC = () => {
  const base = `${API_BASE}/api/v1/external`;
  const sectionTitle: React.CSSProperties = { fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', margin: '22px 0 4px' };
  return (
    <div className="card" style={{ maxWidth: 900 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="card-title">API reference</span>
        <a
          href={`${base}/docs`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary btn-small"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
          title="Open the interactive Swagger UI in a new tab"
        >
          Open live docs ↗
        </a>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 12.5, margin: '0 0 4px' }}>
        Prefer to explore interactively? <strong>Open live docs</strong> launches a Swagger UI where you can
        authorize with your key and run real requests against every endpoint.
      </p>

      {/* Authentication */}
      <div style={sectionTitle}>Authentication</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 8px' }}>
        Send your key in the <InlineCode>X-API-Key</InlineCode> header (or as <InlineCode>Authorization: Bearer art_live_…</InlineCode>).
        Every request is scoped to the key owner's data. The base URL is <InlineCode>{base}</InlineCode>.
      </p>

      {/* Pagination */}
      <div style={sectionTitle}>Pagination</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 8px' }}>
        Offset pagination. Each response includes <InlineCode>total</InlineCode>, <InlineCode>page</InlineCode> and <InlineCode>page_size</InlineCode>.
        Increment <InlineCode>page</InlineCode> until you have all <InlineCode>total</InlineCode> rows. Use <InlineCode>page_size</InlineCode> up
        to <strong>1000</strong> to pull large data sets in fewer calls.
      </p>

      {/* Orders */}
      <div style={sectionTitle}>Orders</div>
      <EndpointRow path="/api/v1/external/orders" />
      <p style={{ color: 'var(--text-muted)', fontSize: 12.5, margin: '2px 0 0' }}>
        Requires the <InlineCode>orders:read</InlineCode> scope. Returns orders with their line items, newest first.
      </p>
      <ParamTable params={ORDER_PARAMS} />
      <ExampleRequest label="Filter by account + marketplace" url={`${base}/orders?marketplace_id=1&seller_id=AVIYOQ3KB9CRN&page_size=500`} />
      <ExampleRequest label="Filter by purchase date range" url={`${base}/orders?purchase_date_from=2026-01-01&purchase_date_to=2026-06-30&page_size=1000`} />

      {/* FBA inventory */}
      <div style={sectionTitle}>FBA inventory</div>
      <EndpointRow path="/api/v1/external/inventory/fba" />
      <p style={{ color: 'var(--text-muted)', fontSize: 12.5, margin: '2px 0 0' }}>
        Requires the <InlineCode>inventory:read</InlineCode> scope.
      </p>
      <ParamTable params={FBA_PARAMS} />
      <ExampleRequest label="In-stock items for one account" url={`${base}/inventory/fba?seller_id=AVIYOQ3KB9CRN&stock_status=in_stock&page_size=1000`} />

      {/* FBM inventory */}
      <div style={sectionTitle}>FBM inventory</div>
      <EndpointRow path="/api/v1/external/inventory/fbm" />
      <p style={{ color: 'var(--text-muted)', fontSize: 12.5, margin: '2px 0 0' }}>
        Requires the <InlineCode>inventory:read</InlineCode> scope.
      </p>
      <ParamTable params={FBM_PARAMS} />
      <ExampleRequest label="Active listings for one account" url={`${base}/inventory/fbm?seller_id=AVIYOQ3KB9CRN&status=Active&page_size=1000`} />
    </div>
  );
};

const keyStatus = (k: ApiKey): { label: string; color: string } => {
  if (!k.is_active) return { label: 'Revoked', color: 'var(--text-muted)' };
  if (k.expires_at && new Date(k.expires_at) < new Date()) return { label: 'Expired', color: 'var(--danger, #dc2626)' };
  return { label: 'Active', color: 'var(--success, #16a34a)' };
};

const Settings: React.FC = () => {
  // settings.view lets a member see this page (nav-gated in App.tsx); mutating
  // API keys — create / reveal / revoke — requires settings.edit.
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('settings.edit');
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Create form
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['orders:read', 'inventory:read']);
  const [expiresInDays, setExpiresInDays] = useState<string>('');
  const [creating, setCreating] = useState(false);

  // The freshly-created key — shown once.
  const [newKey, setNewKey] = useState<ApiKeyCreated | null>(null);
  const [revealed, setRevealed] = useState(false);

  // Revoke confirmation
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listApiKeys();
      setKeys(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleScope = (value: string) => {
    setScopes((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const handleCreate = async () => {
    if (!name.trim()) { setError('Give the key a name'); return; }
    if (scopes.length === 0) { setError('Select at least one scope'); return; }
    setCreating(true);
    setError('');
    try {
      const days = expiresInDays.trim() ? parseInt(expiresInDays, 10) : null;
      const res = await createApiKey({ name: name.trim(), scopes, expires_in_days: days && days > 0 ? days : null });
      setNewKey(res.data);
      setRevealed(false);
      setShowForm(false);
      setName('');
      setScopes(['orders:read', 'inventory:read']);
      setExpiresInDays('');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setError('');
    try {
      await revokeApiKey(id);
      setRevokingId(null);
      setMessage('API key revoked');
      setTimeout(() => setMessage(''), 3000);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const maskedKey = newKey ? `${newKey.key_prefix}${'•'.repeat(28)}` : '';

  return (
    <div>
      <div className="page-header">
        <h2>Settings</h2>
        <p>Manage API keys for external access to your Amazon orders and inventory data</p>
      </div>

      {message && <div className="alert alert-success">{message}</div>}

      {/* One-time secret panel */}
      {newKey && (
        <div className="card" style={{ maxWidth: 900, border: '1px solid var(--accent)', boxShadow: '0 0 0 3px color-mix(in srgb, var(--accent) 12%, transparent)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'color-mix(in srgb, var(--accent) 15%, transparent)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconKey />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Key created — copy it now</div>
              <p style={{ color: 'var(--text-muted)', fontSize: 12.5, margin: '2px 0 0' }}>
                This is the only time the full key is shown. Store it somewhere safe — if you lose it, revoke it and create a new one.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
            <code style={{
              flex: 1, minWidth: 280, padding: '11px 14px', background: 'var(--bg-secondary)',
              border: '1px solid var(--border)', borderRadius: 9, fontSize: 13.5, letterSpacing: 0.3,
              wordBreak: 'break-all', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}>{revealed ? newKey.api_key : maskedKey}</code>
            <button className="btn btn-secondary btn-small" onClick={() => setRevealed((v) => !v)}>{revealed ? 'Hide' : 'Reveal'}</button>
            <CopyButton text={newKey.api_key} label="Copy key" variant="primary" title="Copy the full API key" />
            <button className="btn btn-secondary btn-small" onClick={() => setNewKey(null)}>Done</button>
          </div>
        </div>
      )}

      {/* API keys card */}
      <div className="card" style={{ maxWidth: 900, padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 20px', margin: 0, borderBottom: '1px solid var(--border)' }}>
          <div>
            <span className="card-title">API Keys</span>
            <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--text-muted)' }}>{keys.length} {keys.length === 1 ? 'key' : 'keys'}</span>
          </div>
          {canEdit && !showForm && (
            <button className="btn btn-primary btn-small" onClick={() => { setShowForm(true); setError(''); }}>
              <IconPlus /> Create Key
            </button>
          )}
        </div>

        {error && <div className="alert alert-error" style={{ margin: '16px 20px 0' }}>{error}</div>}

        {/* Create form */}
        {showForm && (
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme ERP integration" maxLength={100} autoFocus />
              </div>
              <div className="form-group">
                <label>Expires in (days)</label>
                <input value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="Blank = never expires" />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 4 }}>
              <label>Scopes *</label>
              <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                {ALL_SCOPES.map((s) => {
                  const on = scopes.includes(s.value);
                  return (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => toggleScope(s.value)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                        textAlign: 'left', cursor: 'pointer', padding: '9px 14px', borderRadius: 9, minWidth: 180,
                        border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                        background: on ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : 'var(--bg-primary)',
                        transition: 'all .15s',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600, fontSize: 13, color: on ? 'var(--accent)' : 'var(--text-primary)' }}>
                        <span style={{
                          width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                          border: `1.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                          background: on ? 'var(--accent)' : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                        }}>{on ? <IconCheck /> : null}</span>
                        {s.label}
                      </span>
                      <span style={{ fontSize: 11.5, color: 'var(--text-muted)', paddingLeft: 22 }}>{s.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => { setShowForm(false); setError(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !name.trim() || scopes.length === 0}>
                {creating ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </div>
        )}

        {/* Keys list */}
        {loading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px' }}>Loading...</p>
        ) : keys.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '44px 20px', color: 'var(--text-muted)' }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, background: 'var(--bg-secondary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <IconKey />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No API keys yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Create one to give an external system read access to your data.</div>
          </div>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                  {['Name', 'Key', 'Scopes', 'Status', 'Last used', 'Created', ''].map((h) => (
                    <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => {
                  const st = keyStatus(k);
                  return (
                    <tr key={k.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}><IconKey /></span>
                          <span style={{ fontWeight: 600 }}>{k.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <code style={{ fontSize: 12.5, padding: '3px 8px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 6 }}>{k.key_prefix}…</code>
                          {canEdit && <RevealCopyButton apiKey={k} onError={(m) => setError(m)} />}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        {k.scopes.map((s) => (
                          <span key={s} style={{ display: 'inline-block', fontSize: 10.5, marginRight: 5, padding: '3px 8px', borderRadius: 11, background: 'color-mix(in srgb, var(--accent) 10%, transparent)', color: 'var(--accent)', fontWeight: 600, whiteSpace: 'nowrap' }}>{s}</span>
                        ))}
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: st.color }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: st.color }} />
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '13px 16px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDateTime(k.last_used_at)}</td>
                      <td style={{ padding: '13px 16px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(k.created_at)}</td>
                      <td style={{ padding: '13px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {!canEdit ? (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        ) : revokingId === k.id ? (
                          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Revoke?</span>
                            <button className="btn btn-danger btn-small" onClick={() => handleRevoke(k.id)}>Yes</button>
                            <button className="btn btn-secondary btn-small" onClick={() => setRevokingId(null)}>No</button>
                          </span>
                        ) : (
                          <button className="btn btn-secondary btn-small" onClick={() => setRevokingId(k.id)}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* API reference */}
      <ApiReference />
    </div>
  );
};

export default Settings;
